import React, { useState, useEffect, useMemo } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { firestore } from '../firebase';
import { getDayDateString } from '../utils/eventUtils';
import {
  DEFAULT_SCHEDULE_FIELDS,
  SCHEDULE_CERTAINTY_CONFIRMED,
  SCHEDULE_CERTAINTY_PRELIMINARY,
  buildScheduleHtmlFromFields,
  defaultAddOnCallout,
  defaultScheduleCta,
  migrateScheduleFieldsForEditor,
  normalizeScheduleSlugFields,
  sortScheduleFieldsByTime,
  validateScheduleStableSlugs,
} from '../constants/defaultSchedule';
import ScheduleStructuredLayout from './schedule/ScheduleStructuredLayout';
import { fetchSiteRegistrationSettings } from '../utils/siteSettings';
import { getPublicSiteMarketingConfig } from '../utils/publicSiteConfig';
import './SchedulePage.css';

function getEventYear(ev) {
  if (ev?.editionYear != null && Number.isFinite(Number(ev.editionYear))) {
    return Number(ev.editionYear);
  }
  const m = (ev?.date || '').toString().trim().match(/^(\d{4})-\d{2}-\d{2}$/);
  if (m) return parseInt(m[1], 10);
  return null;
}

export default function ScheduleEditorPanel({
  events,
  jumpEventId,
  onJumpConsumed,
  onScheduleSaved,
  onError,
}) {
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [scheduleFields, setScheduleFields] = useState(null);
  const [scheduleSaving, setScheduleSaving] = useState(false);
  const [previewRegistrationOpen, setPreviewRegistrationOpen] = useState(true);
  const [registrationOpen, setRegistrationOpen] = useState(false);
  const [registrationUrl, setRegistrationUrl] = useState('');
  const [siteInterestUrl, setSiteInterestUrl] = useState('');
  const [marketingHomeUrl, setMarketingHomeUrl] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [reg, mkt] = await Promise.all([
          fetchSiteRegistrationSettings(),
          getPublicSiteMarketingConfig(),
        ]);
        if (!cancelled) {
          setRegistrationOpen(reg.registrationOpen);
          setRegistrationUrl(reg.registrationUrl);
          setSiteInterestUrl(reg.interestListUrl);
          setMarketingHomeUrl(mkt.marketingSiteBaseUrl || '');
          setPreviewRegistrationOpen(reg.registrationOpen);
        }
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!jumpEventId || !events.length) return;
    if (events.some((e) => e.id === jumpEventId)) {
      setSelectedEventId(jumpEventId);
      onJumpConsumed?.();
    }
  }, [jumpEventId, events, onJumpConsumed]);

  useEffect(() => {
    if (!selectedEventId) {
      setScheduleFields(null);
      return;
    }
    const ev = events.find((e) => e.id === selectedEventId);
    if (!ev) {
      setScheduleFields(null);
      return;
    }
    const existing = ev.scheduleData || null;
    const base = existing
      ? JSON.parse(JSON.stringify(existing))
      : JSON.parse(JSON.stringify(DEFAULT_SCHEDULE_FIELDS));
    setScheduleFields(migrateScheduleFieldsForEditor(base));
  }, [selectedEventId, events]);

  const selectedEvent = useMemo(
    () => events.find((e) => e.id === selectedEventId) || null,
    [events, selectedEventId]
  );

  const fallbackInterestUrl = (
    (scheduleFields?.cta?.interestListUrl || '').trim() ||
    siteInterestUrl ||
    marketingHomeUrl ||
    ''
  ).trim();

  const updateScheduleTopField = (key, value) => {
    setScheduleFields((prev) => (prev ? { ...prev, [key]: value } : prev));
  };

  const updateScheduleAddOn = (key, value) => {
    setScheduleFields((prev) => {
      if (!prev) return prev;
      const cur = { ...defaultAddOnCallout(), ...prev.addOnCallout };
      return { ...prev, addOnCallout: { ...cur, [key]: value } };
    });
  };

  const updateScheduleCtaField = (key, value) => {
    setScheduleFields((prev) => {
      if (!prev) return prev;
      const cur = { ...defaultScheduleCta(), ...prev.cta };
      return { ...prev, cta: { ...cur, [key]: value } };
    });
  };

  const updateScheduleDayHeader = (dayId, value) => {
    setScheduleFields((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        days: prev.days.map((day) => (day.id === dayId ? { ...day, header: value } : day)),
      };
    });
  };

  const updateScheduleDayField = (dayId, key, value) => {
    setScheduleFields((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        days: prev.days.map((day) => (day.id === dayId ? { ...day, [key]: value } : day)),
      };
    });
  };

  const updateScheduleAllWeekLinesFromText = (text) => {
    const lines = String(text)
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean);
    setScheduleFields((prev) => (prev ? { ...prev, allWeekLines: lines } : prev));
  };

  const updateScheduleRowField = (dayId, rowId, key, value) => {
    setScheduleFields((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        days: prev.days.map((day) => {
          if (day.id !== dayId) return day;
          return {
            ...day,
            rows: day.rows.map((row) => {
              if (row.id !== rowId) return row;
              return { ...row, [key]: value };
            }),
          };
        }),
      };
    });
  };

  const updateScheduleNestedItemField = (dayId, rowId, itemId, key, value) => {
    setScheduleFields((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        days: prev.days.map((day) => {
          if (day.id !== dayId) return day;
          return {
            ...day,
            rows: day.rows.map((row) => {
              if (row.id !== rowId || !Array.isArray(row.items)) return row;
              return {
                ...row,
                items: row.items.map((item) =>
                  item.id === itemId ? { ...item, [key]: value } : item
                ),
              };
            }),
          };
        }),
      };
    });
  };

  const moveScheduleRow = (dayId, rowId, delta) => {
    setScheduleFields((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        days: prev.days.map((day) => {
          if (day.id !== dayId) return day;
          const idx = day.rows.findIndex((r) => r.id === rowId);
          if (idx < 0) return day;
          const j = idx + delta;
          if (j < 0 || j >= day.rows.length) return day;
          const rows = [...day.rows];
          [rows[idx], rows[j]] = [rows[j], rows[idx]];
          return { ...day, rows };
        }),
      };
    });
  };

  const addScheduleRow = (dayId) => {
    setScheduleFields((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        days: prev.days.map((day) => {
          if (day.id !== dayId) return day;
          const newRowId = `${day.id}-row-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
          const newRow = {
            id: newRowId,
            type: 'normal',
            time: '',
            label: '',
            note: '',
            timeKind: 'exact',
            stableSlug: '',
          };
          return { ...day, rows: [...day.rows, newRow] };
        }),
      };
    });
  };

  const removeScheduleRow = (dayId, rowId) => {
    setScheduleFields((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        days: prev.days.map((day) => {
          if (day.id !== dayId) return day;
          return { ...day, rows: day.rows.filter((row) => row.id !== rowId) };
        }),
      };
    });
  };

  const addScheduleNestedItem = (dayId, rowId) => {
    setScheduleFields((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        days: prev.days.map((day) => {
          if (day.id !== dayId) return day;
          return {
            ...day,
            rows: day.rows.map((row) => {
              if (row.id !== rowId) return row;
              const newItemId = `${row.id}-item-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
              const items = Array.isArray(row.items) ? row.items : [];
              const newItem = {
                id: newItemId,
                time: '',
                label: '',
                note: '',
                timeKind: 'exact',
                stableSlug: '',
              };
              return { ...row, items: [...items, newItem] };
            }),
          };
        }),
      };
    });
  };

  const removeScheduleNestedItem = (dayId, rowId, itemId) => {
    setScheduleFields((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        days: prev.days.map((day) => {
          if (day.id !== dayId) return day;
          return {
            ...day,
            rows: day.rows.map((row) => {
              if (row.id !== rowId || !Array.isArray(row.items)) return row;
              return { ...row, items: row.items.filter((item) => item.id !== itemId) };
            }),
          };
        }),
      };
    });
  };

  const handleLoadExampleSchedule = () => {
    const cloned = JSON.parse(JSON.stringify(DEFAULT_SCHEDULE_FIELDS));
    setScheduleFields(migrateScheduleFieldsForEditor(cloned));
  };

  const handleSortRowsByTime = () => {
    setScheduleFields((prev) => (prev ? sortScheduleFieldsByTime(prev) : prev));
  };

  const handleSaveSchedule = async () => {
    if (!selectedEventId || !scheduleFields) return;
    const slugErrors = validateScheduleStableSlugs(scheduleFields);
    if (slugErrors.length) {
      onError?.(slugErrors.join(' '));
      return;
    }
    setScheduleSaving(true);
    try {
      const normalized = normalizeScheduleSlugFields(scheduleFields);
      const scheduleHtml = buildScheduleHtmlFromFields(normalized);
      await updateDoc(doc(firestore, 'events', selectedEventId), {
        scheduleData: normalized,
        scheduleHtml,
      });
      onScheduleSaved?.(selectedEventId, { scheduleData: normalized, scheduleHtml });
    } catch (err) {
      onError?.(err.message || 'Save failed');
    } finally {
      setScheduleSaving(false);
    }
  };

  const sortedEvents = useMemo(
    () =>
      events
        .slice()
        .sort((a, b) => {
          const ya = getEventYear(a) ?? -1;
          const yb = getEventYear(b) ?? -1;
          if (ya !== yb) return yb - ya;
          return (a.title || '').toString().localeCompare((b.title || '').toString());
        }),
    [events]
  );

  if (!events.length) {
    return <p className="event-management-empty">No events to edit.</p>;
  }

  return (
    <div className="schedule-editor-panel">
      <div className="schedule-editor-toolbar">
        <label className="schedule-editor-event-pick">
          <span>Event</span>
          <select
            value={selectedEventId || ''}
            onChange={(e) => setSelectedEventId(e.target.value || null)}
          >
            <option value="">Select an event…</option>
            {sortedEvents.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.title}
                {getEventYear(ev) != null ? ` (${getEventYear(ev)})` : ''}
              </option>
            ))}
          </select>
        </label>
        <div className="schedule-editor-toolbar-actions">
          <button type="button" className="btn btn-outline" onClick={handleSortRowsByTime}>
            Sort rows by time
          </button>
          <button type="button" className="btn btn-outline" onClick={handleLoadExampleSchedule}>
            Load example schedule
          </button>
          <button
            type="button"
            className="btn btn-fm-primary"
            onClick={handleSaveSchedule}
            disabled={!selectedEventId || !scheduleFields || scheduleSaving}
          >
            {scheduleSaving ? 'Saving…' : 'Save schedule'}
          </button>
        </div>
      </div>

      {!selectedEventId || !scheduleFields ? (
        <p className="schedule-editor-hint">Choose an event to edit its public schedule.</p>
      ) : (
        <div className="schedule-editor-split">
          <div className="schedule-editor-form-col">
            <div className="schedule-quick-editor schedule-quick-editor--embedded">
              <h4 className="schedule-quick-title">Schedule content</h4>
              <p className="schedule-quick-help">
                Anchor slugs power stable URLs like <code>/schedule#saturday-main</code> (lowercase,
                hyphens). Saving preserves row order; use &quot;Sort rows by time&quot; to reorder by
                clock time.
              </p>

              <div className="schedule-editor-certainty" role="group" aria-label="Schedule status">
                <span className="schedule-editor-certainty__label">Public schedule status</span>
                <div className="schedule-editor-certainty__options">
                  <label>
                    <input
                      type="radio"
                      name="scheduleCertainty"
                      checked={
                        (scheduleFields.scheduleCertainty || SCHEDULE_CERTAINTY_CONFIRMED) ===
                        SCHEDULE_CERTAINTY_CONFIRMED
                      }
                      onChange={() =>
                        updateScheduleTopField('scheduleCertainty', SCHEDULE_CERTAINTY_CONFIRMED)
                      }
                    />
                    <span>
                      <strong>Confirmed</strong>
                      <span className="schedule-editor-certainty__option-sub">
                        {' '}
                        — shown as the current plan (no tentative banner).
                      </span>
                    </span>
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="scheduleCertainty"
                      checked={scheduleFields.scheduleCertainty === SCHEDULE_CERTAINTY_PRELIMINARY}
                      onChange={() =>
                        updateScheduleTopField('scheduleCertainty', SCHEDULE_CERTAINTY_PRELIMINARY)
                      }
                    />
                    <span>
                      <strong>Preliminary / tentative</strong>
                      <span className="schedule-editor-certainty__option-sub">
                        {' '}
                        — visitors see a clear &quot;preliminary schedule&quot; notice above the page.
                      </span>
                    </span>
                  </label>
                </div>
              </div>

              <div className="form-group schedule-quick-top">
                <label>Brand line (eyebrow)</label>
                <input
                  type="text"
                  value={scheduleFields.title}
                  onChange={(e) => updateScheduleTopField('title', e.target.value)}
                />
              </div>
              <div className="form-group schedule-quick-top">
                <label>Page title (H1)</label>
                <input
                  type="text"
                  value={scheduleFields.heroTitle || ''}
                  onChange={(e) => updateScheduleTopField('heroTitle', e.target.value)}
                />
              </div>
              <div className="form-group schedule-quick-top">
                <label>Tagline (optional, under H1)</label>
                <input
                  type="text"
                  value={scheduleFields.subtitle || ''}
                  onChange={(e) => updateScheduleTopField('subtitle', e.target.value)}
                />
              </div>
              <div className="form-group schedule-quick-top">
                <label>Intro paragraph</label>
                <textarea
                  rows={4}
                  value={scheduleFields.introBody || ''}
                  onChange={(e) => updateScheduleTopField('introBody', e.target.value)}
                />
              </div>

              <h4 className="schedule-quick-title">Add-on callout</h4>
              <div className="form-group schedule-quick-top">
                <label>Title</label>
                <input
                  type="text"
                  value={scheduleFields.addOnCallout?.title || ''}
                  onChange={(e) => updateScheduleAddOn('title', e.target.value)}
                />
              </div>
              <div className="form-group schedule-quick-top">
                <label>Body text</label>
                <textarea
                  rows={3}
                  value={scheduleFields.addOnCallout?.body || ''}
                  onChange={(e) => updateScheduleAddOn('body', e.target.value)}
                />
              </div>
              <div className="form-group schedule-quick-top">
                <label>Optional link URL (defaults to registration URL when open)</label>
                <input
                  type="text"
                  value={scheduleFields.addOnCallout?.linkUrl || ''}
                  onChange={(e) => updateScheduleAddOn('linkUrl', e.target.value)}
                  placeholder="https://…"
                />
              </div>
              <div className="form-group schedule-quick-top">
                <label>Link label</label>
                <input
                  type="text"
                  value={scheduleFields.addOnCallout?.linkLabel || ''}
                  onChange={(e) => updateScheduleAddOn('linkLabel', e.target.value)}
                />
              </div>

              <h4 className="schedule-quick-title">Bottom CTA</h4>
              <div className="form-group schedule-quick-top">
                <label>Headline</label>
                <input
                  type="text"
                  value={scheduleFields.cta?.headline || ''}
                  onChange={(e) => updateScheduleCtaField('headline', e.target.value)}
                />
              </div>
              <div className="form-group schedule-quick-top">
                <label>Button label (registration open)</label>
                <input
                  type="text"
                  value={scheduleFields.cta?.buttonLabel || ''}
                  onChange={(e) => updateScheduleCtaField('buttonLabel', e.target.value)}
                />
              </div>
              <div className="form-group schedule-quick-top">
                <label>Closed / interest label</label>
                <input
                  type="text"
                  value={scheduleFields.cta?.closedButtonLabel || ''}
                  onChange={(e) => updateScheduleCtaField('closedButtonLabel', e.target.value)}
                />
              </div>
              <div className="form-group schedule-quick-top">
                <label>Interest list URL (optional)</label>
                <input
                  type="text"
                  value={scheduleFields.cta?.interestListUrl || ''}
                  onChange={(e) => updateScheduleCtaField('interestListUrl', e.target.value)}
                  placeholder="https://…"
                />
              </div>

              <div className="form-group schedule-quick-top">
                <label>Footer note</label>
                <input
                  type="text"
                  value={scheduleFields.footerNote || ''}
                  onChange={(e) => updateScheduleTopField('footerNote', e.target.value)}
                />
              </div>
              <div className="form-group schedule-quick-top">
                <label>All-week card slug (optional)</label>
                <input
                  type="text"
                  value={scheduleFields.allWeekSlug || ''}
                  onChange={(e) => updateScheduleTopField('allWeekSlug', e.target.value)}
                  placeholder="e.g. all-week-long"
                />
              </div>
              <div className="form-group schedule-quick-top">
                <label>All-week bullet lines (one per line)</label>
                <textarea
                  rows={5}
                  value={(scheduleFields.allWeekLines || []).join('\n')}
                  onChange={(e) => updateScheduleAllWeekLinesFromText(e.target.value)}
                  placeholder="Perks that run all week…"
                />
              </div>

              {scheduleFields.days.map((day) => {
                const dayDateStr = selectedEvent ? getDayDateString(selectedEvent, day.id) : '';
                return (
                  <div key={day.id} className="schedule-quick-day">
                    <div className="schedule-quick-day-header-wrap">
                      <label className="schedule-quick-day-header-label">
                        Day title (legacy / detail header)
                      </label>
                      {dayDateStr && (
                        <span className="schedule-quick-day-date" aria-hidden="true">
                          {dayDateStr}
                        </span>
                      )}
                      <input
                        type="text"
                        className="schedule-quick-day-header-input"
                        value={day.header}
                        onChange={(e) => updateScheduleDayHeader(day.id, e.target.value)}
                        placeholder="e.g. WEDNESDAY – Welcome Day"
                      />
                    </div>
                    <div className="schedule-quick-day-meta">
                      <label className="schedule-quick-meta-label">
                        Day anchor slug
                        <input
                          type="text"
                          value={day.stableSlug || ''}
                          onChange={(e) => updateScheduleDayField(day.id, 'stableSlug', e.target.value)}
                          placeholder="e.g. wednesday"
                        />
                      </label>
                      <label className="schedule-quick-meta-label">
                        Day label (black header, right)
                        <input
                          type="text"
                          value={day.dayLabel || ''}
                          onChange={(e) => updateScheduleDayField(day.id, 'dayLabel', e.target.value)}
                          placeholder="e.g. Arrival Day"
                        />
                      </label>
                      <label className="schedule-quick-meta-label">
                        Accent color (hex)
                        <input
                          type="text"
                          value={day.accentColor || ''}
                          onChange={(e) => updateScheduleDayField(day.id, 'accentColor', e.target.value)}
                          placeholder="#3D3D3D"
                        />
                      </label>
                      <label className="schedule-quick-meta-label">
                        Card emoji
                        <input
                          type="text"
                          value={day.cardEmoji || ''}
                          onChange={(e) => updateScheduleDayField(day.id, 'cardEmoji', e.target.value)}
                          placeholder="🛬"
                          maxLength={8}
                        />
                      </label>
                      <label className="schedule-quick-meta-label schedule-quick-meta-wide">
                        Card subtitle
                        <input
                          type="text"
                          value={day.cardSubtitle || ''}
                          onChange={(e) => updateScheduleDayField(day.id, 'cardSubtitle', e.target.value)}
                          placeholder="Short line"
                        />
                      </label>
                      <label className="schedule-quick-meta-label schedule-quick-meta-full">
                        Card body (one line per paragraph)
                        <textarea
                          rows={3}
                          value={(day.cardBodyLines || []).join('\n')}
                          onChange={(e) =>
                            updateScheduleDayField(
                              day.id,
                              'cardBodyLines',
                              String(e.target.value)
                                .split('\n')
                                .map((l) => l.trim())
                                .filter(Boolean)
                            )
                          }
                        />
                      </label>
                    </div>
                    {day.rows.map((row, rowIndex) => {
                      if (row.type === 'normal') {
                        return (
                          <div key={row.id} className="schedule-quick-row-wrap">
                            <div className="schedule-quick-row">
                              <input
                                className="schedule-quick-time"
                                type="text"
                                value={row.time}
                                onChange={(e) =>
                                  updateScheduleRowField(day.id, row.id, 'time', e.target.value)
                                }
                              />
                              <input
                                className="schedule-quick-label"
                                type="text"
                                value={row.label}
                                onChange={(e) =>
                                  updateScheduleRowField(day.id, row.id, 'label', e.target.value)
                                }
                              />
                            </div>
                            <textarea
                              className="schedule-quick-note"
                              rows={2}
                              placeholder="Note (optional)"
                              value={row.note || ''}
                              onChange={(e) =>
                                updateScheduleRowField(day.id, row.id, 'note', e.target.value)
                              }
                            />
                            <div className="schedule-quick-row-slug">
                              <label>
                                Row slug
                                <input
                                  type="text"
                                  value={row.stableSlug || ''}
                                  onChange={(e) =>
                                    updateScheduleRowField(day.id, row.id, 'stableSlug', e.target.value)
                                  }
                                  placeholder="optional"
                                />
                              </label>
                              <label>
                                Time style
                                <select
                                  value={row.timeKind === 'generic' ? 'generic' : 'exact'}
                                  onChange={(e) =>
                                    updateScheduleRowField(day.id, row.id, 'timeKind', e.target.value)
                                  }
                                >
                                  <option value="exact">Clock / specific</option>
                                  <option value="generic">Generic (e.g. Morning)</option>
                                </select>
                              </label>
                            </div>
                            <div className="schedule-quick-row-flags">
                              <button
                                type="button"
                                className="schedule-quick-move"
                                onClick={() => moveScheduleRow(day.id, row.id, -1)}
                                disabled={rowIndex === 0}
                                title="Move up"
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                className="schedule-quick-move"
                                onClick={() => moveScheduleRow(day.id, row.id, 1)}
                                disabled={rowIndex >= day.rows.length - 1}
                                title="Move down"
                              >
                                ↓
                              </button>
                              <label className="schedule-quick-flag">
                                <input
                                  type="checkbox"
                                  checked={!!row.emphasis}
                                  onChange={(e) =>
                                    updateScheduleRowField(
                                      day.id,
                                      row.id,
                                      'emphasis',
                                      e.target.checked
                                    )
                                  }
                                />
                                <span>Bold label</span>
                              </label>
                              <label className="schedule-quick-flag">
                                <input
                                  type="checkbox"
                                  checked={!!row.highlight}
                                  onChange={(e) =>
                                    updateScheduleRowField(
                                      day.id,
                                      row.id,
                                      'highlight',
                                      e.target.checked
                                    )
                                  }
                                />
                                <span>Highlight row</span>
                              </label>
                              <button
                                type="button"
                                className="schedule-quick-row-remove"
                                onClick={() => removeScheduleRow(day.id, row.id)}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        );
                      }
                      if (row.type === 'callout' && row.variant === 'fri') {
                        return (
                          <div key={row.id} className="schedule-quick-row-callout">
                            <div className="schedule-quick-row-label">Friday callout</div>
                            <div className="schedule-quick-row-flags" style={{ marginBottom: 8 }}>
                              <button
                                type="button"
                                className="schedule-quick-move"
                                onClick={() => moveScheduleRow(day.id, row.id, -1)}
                                disabled={rowIndex === 0}
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                className="schedule-quick-move"
                                onClick={() => moveScheduleRow(day.id, row.id, 1)}
                                disabled={rowIndex >= day.rows.length - 1}
                              >
                                ↓
                              </button>
                            </div>
                            <input
                              className="schedule-quick-time"
                              type="text"
                              value={row.time}
                              onChange={(e) =>
                                updateScheduleRowField(day.id, row.id, 'time', e.target.value)
                              }
                            />
                            <input
                              className="schedule-quick-label"
                              type="text"
                              value={row.label}
                              onChange={(e) =>
                                updateScheduleRowField(day.id, row.id, 'label', e.target.value)
                              }
                            />
                            <input
                              className="schedule-quick-note"
                              type="text"
                              value={row.note || ''}
                              onChange={(e) =>
                                updateScheduleRowField(day.id, row.id, 'note', e.target.value)
                              }
                              placeholder="Note (optional)"
                            />
                            <div className="schedule-quick-row-slug">
                              <label>
                                Row slug
                                <input
                                  type="text"
                                  value={row.stableSlug || ''}
                                  onChange={(e) =>
                                    updateScheduleRowField(day.id, row.id, 'stableSlug', e.target.value)
                                  }
                                  placeholder="optional"
                                />
                              </label>
                              <label>
                                Time style
                                <select
                                  value={row.timeKind === 'generic' ? 'generic' : 'exact'}
                                  onChange={(e) =>
                                    updateScheduleRowField(day.id, row.id, 'timeKind', e.target.value)
                                  }
                                >
                                  <option value="exact">Clock / specific</option>
                                  <option value="generic">Generic</option>
                                </select>
                              </label>
                            </div>
                          </div>
                        );
                      }
                      if (row.type === 'main-block') {
                        const satDateStr = selectedEvent
                          ? getDayDateString(selectedEvent, 'saturday')
                          : '';
                        const satLabel = satDateStr
                          ? `Saturday, ${satDateStr} main block`
                          : 'Saturday main block';
                        return (
                          <div key={row.id} className="schedule-quick-row-main">
                            <div className="schedule-quick-row-label">{satLabel}</div>
                            <div className="schedule-quick-row-flags" style={{ marginBottom: 8 }}>
                              <button
                                type="button"
                                className="schedule-quick-move"
                                onClick={() => moveScheduleRow(day.id, row.id, -1)}
                                disabled={rowIndex === 0}
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                className="schedule-quick-move"
                                onClick={() => moveScheduleRow(day.id, row.id, 1)}
                                disabled={rowIndex >= day.rows.length - 1}
                              >
                                ↓
                              </button>
                            </div>
                            <div className="schedule-quick-row-slug">
                              <label>
                                Block row slug
                                <input
                                  type="text"
                                  value={row.stableSlug || ''}
                                  onChange={(e) =>
                                    updateScheduleRowField(day.id, row.id, 'stableSlug', e.target.value)
                                  }
                                  placeholder="e.g. saturday-main"
                                />
                              </label>
                            </div>
                            {row.items?.map((item) => (
                              <div key={item.id} className="schedule-quick-row-wrap">
                                <div className="schedule-quick-row">
                                  <input
                                    className="schedule-quick-time"
                                    type="text"
                                    value={item.time}
                                    onChange={(e) =>
                                      updateScheduleNestedItemField(
                                        day.id,
                                        row.id,
                                        item.id,
                                        'time',
                                        e.target.value
                                      )
                                    }
                                  />
                                  <input
                                    className="schedule-quick-label"
                                    type="text"
                                    value={item.label}
                                    onChange={(e) =>
                                      updateScheduleNestedItemField(
                                        day.id,
                                        row.id,
                                        item.id,
                                        'label',
                                        e.target.value
                                      )
                                    }
                                  />
                                  <button
                                    type="button"
                                    className="schedule-quick-row-remove"
                                    onClick={() =>
                                      removeScheduleNestedItem(day.id, row.id, item.id)
                                    }
                                  >
                                    Remove
                                  </button>
                                </div>
                                <textarea
                                  className="schedule-quick-note"
                                  rows={2}
                                  placeholder="Note (optional)"
                                  value={item.note || ''}
                                  onChange={(e) =>
                                    updateScheduleNestedItemField(
                                      day.id,
                                      row.id,
                                      item.id,
                                      'note',
                                      e.target.value
                                    )
                                  }
                                />
                                <div className="schedule-quick-row-slug">
                                  <label>
                                    Item slug
                                    <input
                                      type="text"
                                      value={item.stableSlug || ''}
                                      onChange={(e) =>
                                        updateScheduleNestedItemField(
                                          day.id,
                                          row.id,
                                          item.id,
                                          'stableSlug',
                                          e.target.value
                                        )
                                      }
                                      placeholder="optional"
                                    />
                                  </label>
                                  <label>
                                    Time style
                                    <select
                                      value={item.timeKind === 'generic' ? 'generic' : 'exact'}
                                      onChange={(e) =>
                                        updateScheduleNestedItemField(
                                          day.id,
                                          row.id,
                                          item.id,
                                          'timeKind',
                                          e.target.value
                                        )
                                      }
                                    >
                                      <option value="exact">Clock / specific</option>
                                      <option value="generic">Generic</option>
                                    </select>
                                  </label>
                                </div>
                              </div>
                            ))}
                            <input
                              className="schedule-quick-note"
                              type="text"
                              value={row.note || ''}
                              onChange={(e) =>
                                updateScheduleRowField(day.id, row.id, 'note', e.target.value)
                              }
                              placeholder="Block note (optional)"
                            />
                            <button
                              type="button"
                              className="schedule-quick-add-item"
                              onClick={() => addScheduleNestedItem(day.id, row.id)}
                            >
                              Add main-block line
                            </button>
                          </div>
                        );
                      }
                      if (row.type === 'callout' && row.variant === 'sat') {
                        const satDateStr = selectedEvent
                          ? getDayDateString(selectedEvent, 'saturday')
                          : '';
                        const satLabel = satDateStr
                          ? `Saturday, ${satDateStr} evening callout`
                          : 'Saturday evening callout';
                        return (
                          <div key={row.id} className="schedule-quick-row-main">
                            <div className="schedule-quick-row-label">{satLabel}</div>
                            <div className="schedule-quick-row-flags" style={{ marginBottom: 8 }}>
                              <button
                                type="button"
                                className="schedule-quick-move"
                                onClick={() => moveScheduleRow(day.id, row.id, -1)}
                                disabled={rowIndex === 0}
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                className="schedule-quick-move"
                                onClick={() => moveScheduleRow(day.id, row.id, 1)}
                                disabled={rowIndex >= day.rows.length - 1}
                              >
                                ↓
                              </button>
                            </div>
                            <div className="schedule-quick-row-slug">
                              <label>
                                Row slug
                                <input
                                  type="text"
                                  value={row.stableSlug || ''}
                                  onChange={(e) =>
                                    updateScheduleRowField(day.id, row.id, 'stableSlug', e.target.value)
                                  }
                                  placeholder="optional"
                                />
                              </label>
                            </div>
                            {row.items?.map((item) => (
                              <div key={item.id} className="schedule-quick-row-wrap">
                                <div className="schedule-quick-row">
                                  <input
                                    className="schedule-quick-time"
                                    type="text"
                                    value={item.time}
                                    onChange={(e) =>
                                      updateScheduleNestedItemField(
                                        day.id,
                                        row.id,
                                        item.id,
                                        'time',
                                        e.target.value
                                      )
                                    }
                                  />
                                  <input
                                    className="schedule-quick-label"
                                    type="text"
                                    value={item.label}
                                    onChange={(e) =>
                                      updateScheduleNestedItemField(
                                        day.id,
                                        row.id,
                                        item.id,
                                        'label',
                                        e.target.value
                                      )
                                    }
                                  />
                                  <button
                                    type="button"
                                    className="schedule-quick-row-remove"
                                    onClick={() =>
                                      removeScheduleNestedItem(day.id, row.id, item.id)
                                    }
                                  >
                                    Remove
                                  </button>
                                </div>
                                <textarea
                                  className="schedule-quick-note"
                                  rows={2}
                                  placeholder="Note (optional)"
                                  value={item.note || ''}
                                  onChange={(e) =>
                                    updateScheduleNestedItemField(
                                      day.id,
                                      row.id,
                                      item.id,
                                      'note',
                                      e.target.value
                                    )
                                  }
                                />
                                <div className="schedule-quick-row-slug">
                                  <label>
                                    Item slug
                                    <input
                                      type="text"
                                      value={item.stableSlug || ''}
                                      onChange={(e) =>
                                        updateScheduleNestedItemField(
                                          day.id,
                                          row.id,
                                          item.id,
                                          'stableSlug',
                                          e.target.value
                                        )
                                      }
                                      placeholder="optional"
                                    />
                                  </label>
                                  <label>
                                    Time style
                                    <select
                                      value={item.timeKind === 'generic' ? 'generic' : 'exact'}
                                      onChange={(e) =>
                                        updateScheduleNestedItemField(
                                          day.id,
                                          row.id,
                                          item.id,
                                          'timeKind',
                                          e.target.value
                                        )
                                      }
                                    >
                                      <option value="exact">Clock / specific</option>
                                      <option value="generic">Generic</option>
                                    </select>
                                  </label>
                                </div>
                              </div>
                            ))}
                            <button
                              type="button"
                              className="schedule-quick-add-item"
                              onClick={() => addScheduleNestedItem(day.id, row.id)}
                            >
                              Add callout line
                            </button>
                          </div>
                        );
                      }
                      return null;
                    })}
                    <div className="schedule-quick-add-row-wrap">
                      <button
                        type="button"
                        className="schedule-quick-add-row"
                        onClick={() => addScheduleRow(day.id)}
                      >
                        Add line
                      </button>
                      {day.rows.some(
                        (r) => r.type === 'main-block' || (r.type === 'callout' && r.variant === 'fri')
                      ) && (
                        <button
                          type="button"
                          className="schedule-quick-add-item"
                          onClick={() => {
                            const mainRow = day.rows.find((r) => r.type === 'main-block');
                            if (mainRow) {
                              addScheduleNestedItem(day.id, mainRow.id);
                            }
                          }}
                        >
                          Add main-block line
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="schedule-editor-preview-col">
            <div className="schedule-editor-preview-header">
              <h4>Live preview</h4>
              <label className="schedule-editor-preview-toggle">
                <input
                  type="checkbox"
                  checked={previewRegistrationOpen}
                  onChange={(e) => setPreviewRegistrationOpen(e.target.checked)}
                />
                <span>Preview as registration open</span>
              </label>
            </div>
            <div className="schedule-editor-preview-shell schedule-page schedule-page--marketing">
              <div className="schedule-mkt-container schedule-editor-preview-inner">
                {selectedEvent && scheduleFields ? (
                  <ScheduleStructuredLayout
                    event={selectedEvent}
                    scheduleData={scheduleFields}
                    sponsors={[]}
                    registrationOpen={registrationOpen}
                    registrationUrl={registrationUrl}
                    fallbackInterestUrl={fallbackInterestUrl}
                    expandedPastDays={new Set(scheduleFields.days.map((d) => d.id))}
                    togglePastDay={() => {}}
                    previewRegistrationOpen={previewRegistrationOpen}
                  />
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
